import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';
import moment from 'moment';
import { useWorker } from '@koale/useworker';
import { useWindowSize } from '@react-hook/window-size';

import LoadingData from '../LoadingData';
import NoResults from '../NoResults';

import { getCountStatistics } from './helpers/getCountStatistics';
import { getCountUserStatistics } from './helpers/getCountUserStatistics';

import { useTheme } from '../../ThemeContext';

import {
  Wrapper,
  Box,
  Item,
  Panel,
  PanelInner,
  Titlebox,
  Title,
  TitleDeco,
  TitleButton,
  TitleButtonIcon,
  Content,
  Row,
  SubPanels,
  SubPanel,
  SubPanelInner,
  SubPanelTitle,
  SubPanelValue,
  SubPanelInfo,
  SubPanelInfoLabel,
  SubPanelInfoValue,
  AvatarHolder,
  AvatarPlaceholder,
  Avatar,
  Information,
  Table,
  TableRow,
  TableCell,
  Label,
  Value,
  Strong,
  Button,
  ButtonIcon,
} from './DashboardPanel.style';

import { Breakpoints } from '../../assets/Variables.style';

import roles from '../../roles';

const DashboardPanel = () => {
  const [width] = useWindowSize();
  const history = useHistory();
  const theme = useTheme();
  const intl = useIntl();
  const { auth, profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const profileCompany = useSelector(
    (state) => state.firestore.data['profileCompany']
  );
  const profileCompanyRole =
    profileCompany &&
    profileCompany.admins &&
    profileCompany.admins.length > 0 &&
    profileCompany.admins.find((admin) => admin.id === auth.uid)
      ? 'isAdmin'
      : profileCompany &&
        profileCompany.users &&
        profileCompany.users.length > 0 &&
        profileCompany.users.find((user) => user.id === auth.uid)
      ? 'isUser'
      : undefined;
  const authRole = roles.find(
    (role) => profileCompanyRole && role.id === profileCompanyRole
  );
  const filterStore = useSelector((state) => state.filterStore);
  const dispatch = useDispatch();
  const dataSources = useSelector(
    (state) => state.firestore.ordered['data_sources']
  );
  const statistics = useSelector((state) => state.firestore.data['statistics']);
  const userStatistics = useSelector(
    (state) => state.firestore.ordered['userStatistics']
  );
  const [countStatisticsWorker, { status: workerStatisticsStatus }] = useWorker(
    getCountStatistics
  );
  const [
    countUserStatisticsWorker,
    { status: workerUserStatisticsStatus },
  ] = useWorker(getCountUserStatistics);
  const [countStatistics, setCountStatistics] = useState(undefined);
  const [countUserStatistics, setCountUserStatistics] = useState(undefined);

  const firestoreConnect = [];
  if (isProfileCompany) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      storeAs: 'profileCompany',
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        { collection: 'data_sources', orderBy: [['updatedAt', 'desc']] },
      ],
      storeAs: 'data_sources',
    });
    firestoreConnect.push({
      collection: 'users',
      doc: auth.uid,
      subcollections: [
        {
          collection: 'statistics',
          orderBy: [['createdAt', 'desc']],
          where: [
            [
              'createdAt',
              '>=',
              moment().startOf('month').utc().format('YYYY-MM-DDTHH:mm:ssZ'),
            ],
          ],
        },
      ],
      storeAs: 'userStatistics',
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'statistics',
          doc: 'overall',
        },
      ],
      storeAs: 'statistics',
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    if (statistics) {
      (async () => {
        if (workerStatisticsStatus !== 'RUNNING') {
          const result = await countStatisticsWorker(statistics);
          setCountStatistics(result);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statistics]);

  useEffect(() => {
    if (userStatistics) {
      (async () => {
        if (workerUserStatisticsStatus !== 'RUNNING') {
          const result = await countUserStatisticsWorker(userStatistics);
          setCountUserStatistics(result);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStatistics]);

  const renderStatisticPanel = ({ title, titleDeco, titleHref, content }) => {
    return (
      <Panel theme={theme.dashboardPanel.panel}>
        <PanelInner>
          <Titlebox>
            <Title theme={theme.dashboardPanel.title}>
              {title}
              {titleDeco ? (
                <TitleDeco theme={theme.dashboardPanel.titleDeco}>
                  {titleDeco}
                </TitleDeco>
              ) : (
                ''
              )}
            </Title>
            {titleHref ? (
              <TitleButton
                theme={theme.button.default}
                title={intl.formatMessage({
                  id: 'DASHBOARD_PANEL.GO_TO_MODULE',
                })}
                to={titleHref}
                tiny
                square
              >
                <TitleButtonIcon className='material-icons'>
                  arrow_forward
                </TitleButtonIcon>
              </TitleButton>
            ) : (
              ''
            )}
          </Titlebox>
          <Content>{content && content}</Content>
        </PanelInner>
      </Panel>
    );
  };

  const renderAIPanel = ({ title, titleDeco, titleHref, data }) => {
    return (
      <Panel theme={theme.dashboardPanel.panel}>
        <PanelInner>
          <Titlebox>
            <Title theme={theme.dashboardPanel.title}>
              {title}
              {titleDeco ? (
                <TitleDeco theme={theme.dashboardPanel.titleDeco}>
                  {titleDeco}
                </TitleDeco>
              ) : (
                ''
              )}
            </Title>
            {titleHref ? (
              <TitleButton
                theme={theme.button.default}
                title={intl.formatMessage({
                  id: 'DASHBOARD_PANEL.GO_TO_MODULE',
                })}
                to={titleHref}
                tiny
                square
              >
                <TitleButtonIcon className='material-icons'>
                  arrow_forward
                </TitleButtonIcon>
              </TitleButton>
            ) : (
              ''
            )}
          </Titlebox>
          <Content>
            <SubPanels>
              <SubPanel theme={theme.dashboardPanel.subPanel}>
                <SubPanelInner>
                  {data ? (
                    <>
                      <SubPanelTitle theme={theme.dashboardPanel.subPanelTitle}>
                        {data.progressTitle}
                      </SubPanelTitle>
                      <SubPanelValue theme={theme.dashboardPanel.subPanelValue}>
                        {data.progress}
                      </SubPanelValue>
                    </>
                  ) : (
                    <LoadingData fullmode={true} />
                  )}
                </SubPanelInner>
              </SubPanel>
              <SubPanel theme={theme.dashboardPanel.subPanel}>
                <SubPanelInner>
                  {data ? (
                    <>
                      <SubPanelTitle theme={theme.dashboardPanel.subPanelTitle}>
                        {data.doneTitle}
                      </SubPanelTitle>
                      <SubPanelValue theme={theme.dashboardPanel.subPanelValue}>
                        {data.done}
                      </SubPanelValue>
                    </>
                  ) : (
                    <LoadingData fullmode={true} />
                  )}
                </SubPanelInner>
              </SubPanel>
            </SubPanels>
          </Content>
        </PanelInner>
      </Panel>
    );
  };

  const renderUserPanel = ({ title, titleDeco, titleHref, data }) => {
    return (
      <Panel theme={theme.dashboardPanel.panel}>
        <PanelInner>
          <Titlebox>
            <Title theme={theme.dashboardPanel.title}>
              {title}
              {titleDeco ? (
                <TitleDeco theme={theme.dashboardPanel.titleDeco}>
                  {titleDeco}
                </TitleDeco>
              ) : (
                ''
              )}
            </Title>
            {titleHref ? (
              <TitleButton
                theme={theme.button.default}
                title={intl.formatMessage({
                  id: 'DASHBOARD_PANEL.GO_TO_MODULE',
                })}
                to={titleHref}
                tiny
                square
              >
                <TitleButtonIcon className='material-icons'>
                  arrow_forward
                </TitleButtonIcon>
              </TitleButton>
            ) : (
              ''
            )}
          </Titlebox>
          <Content>
            <Row>
              <SubPanels>
                <SubPanel theme={theme.dashboardPanel.subPanel} narrow>
                  <SubPanelInner>
                    <SubPanelInfo>
                      <AvatarHolder
                        theme={theme.profile.avatarHolder}
                        title={
                          profile.full_name
                            ? `${profile.full_name}, ${profile.email}`
                            : profile.email
                        }
                      >
                        {profile.avatar ? (
                          <Avatar
                            theme={theme.profile.avatar}
                            src={profile.avatar}
                            alt={
                              profile.full_name
                                ? `${profile.full_name}, ${profile.email}`
                                : profile.email
                            }
                          ></Avatar>
                        ) : (
                          <AvatarPlaceholder theme={theme.profile.avatar} />
                        )}
                      </AvatarHolder>
                      <SubPanelInfoLabel
                        theme={theme.dashboardPanel.subPanelInfoLabel}
                      >
                        {profile.full_name || profile.email}
                      </SubPanelInfoLabel>
                    </SubPanelInfo>
                  </SubPanelInner>
                </SubPanel>
                <SubPanel theme={theme.dashboardPanel.subPanel}>
                  <SubPanelInner>
                    <SubPanelInfo>
                      <SubPanelInfoLabel
                        theme={theme.dashboardPanel.subPanelInfoLabel}
                      >
                        {intl.formatMessage({
                          id: 'DASHBOARD_PANEL.LAST_ACTIVITY',
                        })}
                        :
                      </SubPanelInfoLabel>
                      <SubPanelInfoValue
                        theme={theme.dashboardPanel.subPanelInfoValue}
                      >
                        {profile.loggedIn &&
                          moment(profile.loggedIn).format('YYYY-MM-DD')}
                      </SubPanelInfoValue>
                    </SubPanelInfo>
                  </SubPanelInner>
                </SubPanel>
              </SubPanels>
            </Row>
            <Row>
              <Information theme={theme.dashboardPanel.information}>
                {intl.formatMessage({
                  id: 'DASHBOARD_PANEL.NUMBER_OF_PHOTOS',
                })}
              </Information>
            </Row>
            <Row grow>
              <SubPanels>
                <SubPanel theme={theme.dashboardPanel.subPanel}>
                  <SubPanelInner>
                    {data ? (
                      <>
                        <SubPanelTitle
                          theme={theme.dashboardPanel.subPanelTitle}
                        >
                          {data.dailyTitle}
                        </SubPanelTitle>
                        <SubPanelValue
                          theme={theme.dashboardPanel.subPanelValue}
                        >
                          {data.daily}
                        </SubPanelValue>
                      </>
                    ) : (
                      <LoadingData fullmode={true} />
                    )}
                  </SubPanelInner>
                </SubPanel>
                <SubPanel theme={theme.dashboardPanel.subPanel}>
                  <SubPanelInner>
                    {data ? (
                      <>
                        <SubPanelTitle
                          theme={theme.dashboardPanel.subPanelTitle}
                        >
                          {data.weeklyTitle}
                        </SubPanelTitle>
                        <SubPanelValue
                          theme={theme.dashboardPanel.subPanelValue}
                        >
                          {data.weekly}
                        </SubPanelValue>
                      </>
                    ) : (
                      <LoadingData fullmode={true} />
                    )}
                  </SubPanelInner>
                </SubPanel>
                <SubPanel theme={theme.dashboardPanel.subPanel}>
                  <SubPanelInner>
                    {data ? (
                      <>
                        <SubPanelTitle
                          theme={theme.dashboardPanel.subPanelTitle}
                        >
                          {data.monthlyTitle}
                        </SubPanelTitle>
                        <SubPanelValue
                          theme={theme.dashboardPanel.subPanelValue}
                        >
                          {data.monthly}
                        </SubPanelValue>
                      </>
                    ) : (
                      <LoadingData fullmode={true} />
                    )}
                  </SubPanelInner>
                </SubPanel>
              </SubPanels>
            </Row>
          </Content>
        </PanelInner>
      </Panel>
    );
  };

  return (
    <Wrapper>
      <Box>
        <Item>
          {renderStatisticPanel({
            title: intl.formatMessage({ id: 'DASHBOARD_PANEL.DATA_SOURCES' }),
            titleDeco: dataSources && dataSources.length,
            titleHref: '/data-sources',
            content: isLoaded(dataSources) ? (
              <>
                {dataSources && dataSources.length > 0 ? (
                  <Table>
                    {width > Breakpoints.sm ? (
                      <TableRow
                        theme={theme.dashboardPanel.tableRow.head}
                        head
                        gridTemplateColumns={'1fr 1fr 50px'}
                      >
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.NAME' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.DATE' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        ></TableCell>
                      </TableRow>
                    ) : (
                      ''
                    )}
                    {dataSources.slice(0, 5).map((dataSource, key) => (
                      <TableRow
                        theme={theme.dashboardPanel.tableRow.default}
                        key={key}
                        gridTemplateColumns={'1fr 1fr 50px'}
                      >
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.default}
                        >
                          {width <= Breakpoints.sm ? (
                            <Label>
                              {intl.formatMessage({
                                id: 'DASHBOARD_PANEL.NAME',
                              })}
                            </Label>
                          ) : (
                            ''
                          )}
                          {width <= Breakpoints.sm ? (
                            <Value>
                              <Strong>{dataSource.name}</Strong>
                            </Value>
                          ) : (
                            <Strong>{dataSource.name}</Strong>
                          )}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.default}
                        >
                          {width <= Breakpoints.sm ? (
                            <Label>
                              {intl.formatMessage({
                                id: 'DASHBOARD_PANEL.DATE',
                              })}
                            </Label>
                          ) : (
                            ''
                          )}
                          {width <= Breakpoints.sm ? (
                            <Value>
                              <Strong>
                                {dataSource.updatedAt &&
                                  moment(dataSource.updatedAt).format(
                                    'YYYY-MM-DD HH:mm:ss'
                                  )}
                              </Strong>
                            </Value>
                          ) : (
                            <Strong>
                              {dataSource.updatedAt &&
                                moment(dataSource.updatedAt).format(
                                  'YYYY-MM-DD HH:mm:ss'
                                )}
                            </Strong>
                          )}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.default}
                          right
                        >
                          <Button
                            theme={theme.button.transparent}
                            tiny
                            square
                            onClick={() => {
                              history.push({
                                pathname: '/data-sources',
                                search: `?query=${dataSource.id}`,
                              });
                            }}
                          >
                            <ButtonIcon className={'material-icons'}>
                              arrow_forward
                            </ButtonIcon>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>
                ) : (
                  <NoResults fullmode={true} />
                )}
              </>
            ) : (
              <LoadingData fullmode={true} />
            ),
          })}
        </Item>
        <Item>
          {renderStatisticPanel({
            title: intl.formatMessage({ id: 'DASHBOARD_PANEL.VIEWER' }),
            titleHref: '/viewer',
            content: isLoaded(statistics) ? (
              <>
                {countStatistics &&
                Object.entries(countStatistics).length > 0 ? (
                  <Table>
                    {width > Breakpoints.sm ? (
                      <TableRow
                        theme={theme.dashboardPanel.tableRow.head}
                        head
                        gridTemplateColumns={'1fr 100px 50px'}
                      >
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.NAME' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.PHOTOS' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        ></TableCell>
                      </TableRow>
                    ) : (
                      ''
                    )}
                    {Object.entries(countStatistics)
                      .sort(
                        (dataSourceA, dataSourceB) =>
                          dataSourceB[1].total.length -
                          dataSourceA[1].total.length
                      )
                      .slice(0, 5)
                      .map(([id, { total }], key) => (
                        <TableRow
                          theme={theme.dashboardPanel.tableRow.default}
                          key={key}
                          gridTemplateColumns={'1fr 100px 50px'}
                        >
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.NAME',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>
                                  {isLoaded(dataSources) &&
                                    (() => {
                                      const dataSource = dataSources.find(
                                        (dataSource) => dataSource.id === id
                                      );

                                      return (
                                        (dataSource && dataSource.name) || id
                                      );
                                    })()}
                                </Strong>
                              </Value>
                            ) : (
                              <Strong>
                                {isLoaded(dataSources) &&
                                  (() => {
                                    const dataSource = dataSources.find(
                                      (dataSource) => dataSource.id === id
                                    );

                                    return (
                                      (dataSource && dataSource.name) || id
                                    );
                                  })()}
                              </Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.PHOTOS',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>{total.length}</Strong>
                              </Value>
                            ) : (
                              <Strong>{total.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                            right
                          >
                            <Button
                              theme={theme.button.transparent}
                              tiny
                              square
                              onClick={() => {
                                dispatch({
                                  type: 'FILTER_SET',
                                  payload: { ...filterStore, dataSource: id },
                                });
                                history.push({
                                  pathname: '/viewer',
                                });
                              }}
                            >
                              <ButtonIcon className={'material-icons'}>
                                arrow_forward
                              </ButtonIcon>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </Table>
                ) : (
                  <NoResults fullmode={true} />
                )}
              </>
            ) : (
              <LoadingData fullmode={true} />
            ),
          })}
        </Item>
        <Item>
          {renderStatisticPanel({
            title: intl.formatMessage({
              id: 'DASHBOARD_PANEL.DATA_ANNOTATION',
            }),
            titleHref: '/data-annotation',
            content: isLoaded(statistics) ? (
              <>
                {countStatistics &&
                Object.entries(countStatistics).length > 0 ? (
                  <Table>
                    {width > Breakpoints.sm ? (
                      <TableRow
                        theme={theme.dashboardPanel.tableRow.head}
                        head
                        gridTemplateColumns={'1fr 100px 100px 100px 50px'}
                      >
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.NAME' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.PHOTOS' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({
                            id: 'DASHBOARD_PANEL.ANNOTATED',
                          })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({
                            id: 'DASHBOARD_PANEL.WAITING',
                          })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        ></TableCell>
                      </TableRow>
                    ) : (
                      ''
                    )}
                    {Object.entries(countStatistics)
                      .sort(
                        (dataSourceA, dataSourceB) =>
                          dataSourceB[1].total.length -
                          dataSourceA[1].total.length
                      )
                      .slice(0, 5)
                      .map(([id, { total, annotated }], key) => (
                        <TableRow
                          theme={theme.dashboardPanel.tableRow.default}
                          key={key}
                          gridTemplateColumns={'1fr 100px 100px 100px 50px'}
                        >
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.NAME',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>
                                  {isLoaded(dataSources) &&
                                    (() => {
                                      const dataSource = dataSources.find(
                                        (dataSource) => dataSource.id === id
                                      );

                                      return (
                                        (dataSource && dataSource.name) || id
                                      );
                                    })()}
                                </Strong>
                              </Value>
                            ) : (
                              <Strong>
                                {isLoaded(dataSources) &&
                                  (() => {
                                    const dataSource = dataSources.find(
                                      (dataSource) => dataSource.id === id
                                    );

                                    return (
                                      (dataSource && dataSource.name) || id
                                    );
                                  })()}
                              </Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.PHOTOS',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>{total.length}</Strong>
                              </Value>
                            ) : (
                              <Strong>{total.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.ANNOTATED',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>{annotated.length}</Strong>
                              </Value>
                            ) : (
                              <Strong>{annotated.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.WAITING',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>
                                  {total.length - annotated.length}
                                </Strong>
                              </Value>
                            ) : (
                              <Strong>{total.length - annotated.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                            right
                          >
                            <Button
                              theme={theme.button.transparent}
                              tiny
                              square
                              onClick={() => {
                                dispatch({
                                  type: 'FILTER_SET',
                                  payload: { ...filterStore, dataSource: id },
                                });
                                history.push({
                                  pathname: '/data-annotation',
                                });
                              }}
                            >
                              <ButtonIcon className={'material-icons'}>
                                arrow_forward
                              </ButtonIcon>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </Table>
                ) : (
                  <NoResults fullmode={true} />
                )}
              </>
            ) : (
              <LoadingData fullmode={true} />
            ),
          })}
        </Item>
        <Item>
          {renderStatisticPanel({
            title: intl.formatMessage({
              id: 'DASHBOARD_PANEL.ANNOTATION_VERIFICATION',
            }),
            titleHref: '/annotation-verification',
            content: isLoaded(statistics) ? (
              <>
                {countStatistics &&
                Object.entries(countStatistics).length > 0 ? (
                  <Table>
                    {width > Breakpoints.sm ? (
                      <TableRow
                        theme={theme.dashboardPanel.tableRow.head}
                        head
                        gridTemplateColumns={'1fr 100px 100px 100px 50px'}
                      >
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.NAME' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({ id: 'DASHBOARD_PANEL.PHOTOS' })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({
                            id: 'DASHBOARD_PANEL.VERIFIED',
                          })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        >
                          {intl.formatMessage({
                            id: 'DASHBOARD_PANEL.WAITING',
                          })}
                        </TableCell>
                        <TableCell
                          theme={theme.dashboardPanel.tableCell.head}
                          head
                        ></TableCell>
                      </TableRow>
                    ) : (
                      ''
                    )}
                    {Object.entries(countStatistics)
                      .sort(
                        (dataSourceA, dataSourceB) =>
                          dataSourceB[1].total.length -
                          dataSourceA[1].total.length
                      )
                      .slice(0, 5)
                      .map(([id, { total, verified }], key) => (
                        <TableRow
                          theme={theme.dashboardPanel.tableRow.default}
                          key={key}
                          gridTemplateColumns={'1fr 100px 100px 100px 50px'}
                        >
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.NAME',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>
                                  {isLoaded(dataSources) &&
                                    (() => {
                                      const dataSource = dataSources.find(
                                        (dataSource) => dataSource.id === id
                                      );

                                      return (
                                        (dataSource && dataSource.name) || id
                                      );
                                    })()}
                                </Strong>
                              </Value>
                            ) : (
                              <Strong>
                                {isLoaded(dataSources) &&
                                  (() => {
                                    const dataSource = dataSources.find(
                                      (dataSource) => dataSource.id === id
                                    );

                                    return (
                                      (dataSource && dataSource.name) || id
                                    );
                                  })()}
                              </Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.PHOTOS',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>{total.length}</Strong>
                              </Value>
                            ) : (
                              <Strong>{total.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.VERIFIED',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>{verified.length}</Strong>
                              </Value>
                            ) : (
                              <Strong>{verified.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                          >
                            {width <= Breakpoints.sm ? (
                              <Label>
                                {intl.formatMessage({
                                  id: 'DASHBOARD_PANEL.WAITING',
                                })}
                              </Label>
                            ) : (
                              ''
                            )}
                            {width <= Breakpoints.sm ? (
                              <Value>
                                <Strong>
                                  {total.length - verified.length}
                                </Strong>
                              </Value>
                            ) : (
                              <Strong>{total.length - verified.length}</Strong>
                            )}
                          </TableCell>
                          <TableCell
                            theme={theme.dashboardPanel.tableCell.default}
                            right
                          >
                            <Button
                              theme={theme.button.transparent}
                              tiny
                              square
                              onClick={() => {
                                dispatch({
                                  type: 'FILTER_SET',
                                  payload: { ...filterStore, dataSource: id },
                                });
                                history.push({
                                  pathname: '/annotation-verification',
                                });
                              }}
                            >
                              <ButtonIcon className={'material-icons'}>
                                arrow_forward
                              </ButtonIcon>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </Table>
                ) : (
                  <NoResults fullmode={true} />
                )}
              </>
            ) : (
              <LoadingData fullmode={true} />
            ),
          })}
        </Item>
        <Item>
          {renderAIPanel({
            title: intl.formatMessage({ id: 'DASHBOARD_PANEL.AI_TRAINING' }),
            titleHref: '/ai-training',
            data: isLoaded(statistics)
              ? {
                  progressTitle: intl.formatMessage({
                    id: 'DASHBOARD_PANEL.AI_TRAINING_NEURAL_IN_PROGRESS',
                  }),
                  progress: (statistics && statistics.NNInProgress) || 0,
                  doneTitle: intl.formatMessage({
                    id: 'DASHBOARD_PANEL.AI_TRAINING_NEURAL_DONE',
                  }),
                  done: (statistics && statistics.NNDone) || 0,
                }
              : undefined,
          })}
        </Item>
        <Item>
          {renderUserPanel({
            title: intl.formatMessage({
              id: 'DASHBOARD_PANEL.USER_PROFILE',
            }),
            titleDeco:
              isLoaded(profileCompany) &&
              profileCompanyRole &&
              `(${intl.formatMessage({
                id: `ROLE.${authRole.name}`,
                defaultMessage: authRole.name,
              })})`,
            data:
              isLoaded(userStatistics) && countUserStatistics
                ? {
                    dailyTitle: intl.formatMessage({
                      id: 'DASHBOARD_PANEL.USER_TAGGED_DAILY',
                    }),
                    daily: countUserStatistics.daily,
                    weekly: countUserStatistics.weekly,
                    weeklyTitle: intl.formatMessage({
                      id: 'DASHBOARD_PANEL.USER_TAGGED_WEEKLY',
                    }),
                    monthly: countUserStatistics.monthly,
                    monthlyTitle: intl.formatMessage({
                      id: 'DASHBOARD_PANEL.USER_TAGGED_MONTHLY',
                    }),
                  }
                : undefined,
          })}
        </Item>
      </Box>
    </Wrapper>
  );
};

export default DashboardPanel;
